import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CalendarBlank, Camera, Check, Warning, X } from '@phosphor-icons/react';
import { DialogHuelle, FeldRaster, ImmoButton, TextFeld } from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { heuteIso } from '../../../lib/immobilien/format';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { useImmoTheme } from '../../../lib/immobilien/theme';
import { pruefeAblesung } from '../../../lib/immobilien/zaehlerFormular';
import { typInfo } from '../../../lib/zaehler/typen';
import { kurzDatum, standText } from '../../../lib/zaehler/liste';
import { FOTO_BUCKET, korrigiereNachEinfuegen } from '../../../lib/zaehler/zaehlerApi';
import type { AblesungZeile, ZaehlerDaten } from '../../../lib/zaehler/useZaehlerDetail';
import { FehlerBanner, FeldBlock, NotizFeld } from './teile';

/**
 * „Neue Ablesung“ (Handoff Zähler §5.5): Datum, Stand, Foto, Notiz; Verbrauch live gegen die letzte Ablesung
 * vor dem gewählten Datum, Plausibilitätswarnung wie bisher (blockiert nicht). Nach dem Speichern wird bei
 * Nachträgen der Verbrauch der folgenden Ablesung angepasst.
 */
export default function AblesungFormular({ zaehler, ablesungen, nutzerId, onSchliessen, onGespeichert }: {
  zaehler: ZaehlerDaten;
  ablesungen: readonly AblesungZeile[];
  nutzerId: string | null;
  onSchliessen: () => void;
  onGespeichert: (meldung: string, fehler?: boolean) => void;
}) {
  const id = useId();
  const { mobil } = useMobilModus();
  const [theme] = useImmoTheme();
  const typ = typInfo(zaehler.typ);
  const einheit = zaehler.einheit || 'kWh';
  const [datum, setDatum] = useState(heuteIso);
  const [wert, setWert] = useState('');
  const [notizen, setNotizen] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const dateiFeld = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!foto) { setVorschau(null); return; }
    const url = URL.createObjectURL(foto);
    setVorschau(url);
    return () => URL.revokeObjectURL(url);
  }, [foto]);

  const basis = useMemo(() => ablesungen.map(a => ({ wert: a.wert, datum: a.datum })), [ablesungen]);
  const p = pruefeAblesung(wert, datum, basis, einheit);
  // Infobox: letzte Ablesung bis zum gewählten Datum (bei heute die neueste)
  const letzte = useMemo(() => ablesungen.find(a => a.datum <= datum) ?? null, [ablesungen, datum]);

  const wertAendern = (v: string) => { setWert(v); setFehler(null); };

  const speichere = async () => {
    if (p.wert === null) { setFehler('Bitte gültigen Zählerstand eingeben.'); return; }
    setFehler(null);
    setLaeuft(true);
    try {
      let fotoUrl: string | null = null;
      if (foto) {
        const pfad = `${zaehler.id}/${Date.now()}.${foto.name.split('.').pop() || 'jpg'}`;
        const up = await supabase.storage.from(FOTO_BUCKET).upload(pfad, foto);
        if (up.error) throw new Error(`Foto konnte nicht hochgeladen werden (${up.error.message}). Foto entfernen oder erneut versuchen.`);
        fotoUrl = supabase.storage.from(FOTO_BUCKET).getPublicUrl(pfad).data.publicUrl;
      }
      const { data, error } = await supabase.from('ablesungen').insert({
        zaehler_id: zaehler.id, datum, wert: p.wert, verbrauch: p.verbrauch, foto_url: fotoUrl, notizen: notizen.trim() || null, erfasst_von: nutzerId,
      }).select('id, created_at').single();
      if (error) throw new Error(error.message);
      const neu = data as { id: string; created_at: string | null };
      const korrektur = await korrigiereNachEinfuegen(
        [...ablesungen, { id: neu.id, datum, wert: p.wert, verbrauch: p.verbrauch, created_at: neu.created_at }], neu.id);
      onGespeichert(korrektur ? `Ablesung gespeichert – Verbrauch der folgenden Ablesung nicht angepasst (${korrektur})` : 'Ablesung gespeichert', !!korrektur);
    } catch (e) {
      setFehler(`Fehler beim Speichern: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
      setLaeuft(false);
    }
  };

  return (
    <DialogHuelle titel="Neue Ablesung" untertitel={`Zählerstand erfassen · ${zaehler.zaehler_nummer}`} icon={typ.icon}
      iconFarbe={{ farbe: typ.farbe, flaeche: typ.flaeche }} breite={520} vollbild={mobil} onSchliessen={onSchliessen} laeuft={laeuft}
      fuss={(
        <>
          <ImmoButton art="akzent" groesse={48} pille={false} icon={Check} onClick={() => { void speichere(); }} disabled={laeuft}
            style={{ flex: 1, ...(p.warnung ? { background: 'var(--warn)', color: 'var(--bg)' } : {}) }}>
            {p.warnung ? 'Trotzdem speichern' : 'Speichern'}
          </ImmoButton>
          <ImmoButton groesse={48} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ fontSize: 14 }}>Abbrechen</ImmoButton>
        </>
      )}>
      {fehler && <FehlerBanner text={fehler} />}
      {letzte && (
        <div className="flex flex-col" style={{ gap: 2, borderRadius: 18, padding: '14px 16px', background: 'var(--fill2)' }}>
          <div className="flex items-center justify-between" style={{ gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>Letzter Stand</span>
            <b className="immo-zahl" style={{ fontSize: 18, fontWeight: 800 }}>{standText(letzte.wert, einheit)}</b>
          </div>
          <div className="flex items-center justify-between" style={{ gap: 10, fontSize: 12, color: 'var(--ink3)' }}>
            <span>Abgelesen am</span>
            <span className="immo-zahl">{kurzDatum(letzte.datum)}</span>
          </div>
        </div>
      )}
      <FeldRaster minBreite={190}>
        {/* colorScheme: Kalender-Symbol des Browsers auch im Dunkelmodus sichtbar */}
        <TextFeld label="Ablesedatum" type="date" icon={CalendarBlank} wert={datum} onChange={v => setDatum(v || heuteIso())}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 15, fontWeight: 700, color: 'var(--ink)', colorScheme: theme }} />
        <TextFeld label={`Zählerstand (${einheit}) *`} wert={wert} onChange={wertAendern} inputMode="decimal" placeholder="z.B. 12345.67" autoFocus
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 19, fontWeight: 800, color: 'var(--ink)' }} />
      </FeldRaster>
      {p.verbrauch !== null && (
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 8, borderRadius: 16, padding: '12px 16px', background: p.verbrauch < 0 ? 'var(--bad-soft)' : 'var(--ok-soft)' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink2)' }}>Verbrauch seit letzter Ablesung</span>
          <b className="immo-zahl" style={{ fontSize: 17, fontWeight: 800, color: p.verbrauch < 0 ? 'var(--bad)' : 'var(--ok)' }}>{standText(p.verbrauch, einheit)}</b>
        </div>
      )}
      {p.warnung && (
        <div role="status" className="flex items-start" style={{ gap: 9, borderRadius: 16, padding: '12px 14px', background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 700 }}>
          <Warning weight="fill" size={18} style={{ flexShrink: 0, marginTop: 1 }} />{p.warnung}
        </div>
      )}
      <FeldBlock label="Foto (Beleg)" htmlFor={`${id}-foto`}>
        <input ref={dateiFeld} id={`${id}-foto`} type="file" accept="image/*" className="sr-only"
          onChange={ev => { setFoto(ev.target.files?.[0] ?? null); ev.target.value = ''; }} />
        <button type="button" onClick={() => dateiFeld.current?.click()} className="flex items-center w-full text-left cursor-pointer"
          style={{ height: 50, gap: 10, padding: '0 14px', borderRadius: 15, border: '1.5px dashed var(--line)', background: 'transparent', color: 'var(--ink)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}>
          <Camera weight="fill" size={19} color="var(--acc)" />
          <span className="truncate">{foto ? foto.name : 'Foto auswählen...'}</span>
        </button>
        {vorschau && (
          <div className="relative">
            <img src={vorschau} alt="Vorschau" className="w-full" style={{ height: 140, objectFit: 'cover', borderRadius: 14 }} />
            <button type="button" aria-label="Foto entfernen" onClick={() => setFoto(null)} className="absolute flex items-center justify-center cursor-pointer"
              style={{ top: 8, right: 8, width: mobil ? 44 : 30, height: mobil ? 44 : 30, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff' }}>
              <X weight="bold" size={12} />
            </button>
          </div>
        )}
      </FeldBlock>
      <FeldBlock label="Notizen" htmlFor={`${id}-notiz`}>
        <NotizFeld id={`${id}-notiz`} wert={notizen} onChange={setNotizen} />
      </FeldBlock>
    </DialogHuelle>
  );
}
